import { useId } from 'react';
import { useTranslations } from 'use-intl';
import useFormat from '@/hooks/useFormat';
import useCustomRouter from '@/hooks/useCustomRouter';
import { classnames } from '@/utils/classnames/classnames';
import { VariantSelectorProps } from './types';

const VariantSelector = ({ variants, currentSku, currency, onChange, className }: VariantSelectorProps) => {
  const translate = useTranslations();
  const { formatCurrency } = useFormat();
  const router = useCustomRouter();
  const labelId = useId();

  const buildLabel = (variant: VariantSelectorProps['variants'][number]) => {
    // A `0` price means CT enrichment didn't supply a Standalone Price
    // for this variant's SKU. Show "Unavailable" rather than "$0.00" so
    // the dropdown matches the PLP tile / pricing card treatment.
    const effectivePrice = variant.discountedPrice ?? variant.price;
    const priceLabel =
      effectivePrice > 0 ? formatCurrency(effectivePrice, currency) : translate('product.price-unavailable');
    const description = variant.description ? ` — ${variant.description}` : '';
    const oosSuffix = variant.inStock ? '' : ` ${translate('pdp.out-of-stock-suffix')}`;
    return `${variant.name}${description} — ${priceLabel}${oosSuffix}`;
  };

  const handleChange = (sku: string) => {
    const variant = variants.find((v) => v.sku === sku);
    if (variant?.url) {
      router.push(variant.url);
    } else {
      onChange(sku);
    }
  };

  return (
    <div className={classnames('grid gap-3', className)}>
      <label id={labelId} htmlFor={`${labelId}-select`} className="text-14 font-bold text-gray-800">
        {translate('pdp.select-variant')}
      </label>
      <select
        id={`${labelId}-select`}
        value={currentSku}
        onChange={(e) => handleChange(e.target.value)}
        // EWM-429 — `truncate` clips a long selected variant label with an
        // ellipsis instead of letting it overflow the control / PDP layout.
        className="text-15 focus:border-primary w-full truncate rounded-lg border-2 border-gray-300 bg-white px-4 py-3 font-medium text-gray-800 transition-colors hover:border-gray-400 focus:outline-none"
      >
        {variants.map((variant) => (
          <option key={variant.sku} value={variant.sku}>
            {buildLabel(variant)}
          </option>
        ))}
      </select>
    </div>
  );
};

export default VariantSelector;
